import React, { useEffect, useState } from "react";
import { useGetSupplierOverviewData } from "../../../../../hooks/query/emissions-insight/emissions-insights-query";
import { constructQueryParamsString, showErrorToast } from "../../../../../utils";
import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import { useGetSuppliersYearlyData } from "../../../../../hooks/query";
import { calculateLimeGradientColor } from "../../../../ISSB/Dashboard/dashboardDataUtils";
import { nivo_theme } from "../../../../ISSB/Dashboard/components/chart-utils";
import { ResponsiveHeatMap } from "@nivo/heatmap";
import LocalSpinner from "../../../../../components/LocalSpinner";
import SupplierFiltersCatQualityYear from "../../supplier-components/SupplierFiltersCatQualityYear";
import { ResponsivePie } from "@nivo/pie";
import { colors } from "../../../../../theme/foundations/colors";

const SupplierListVisual = ({ filters, setFilters, showHeatMap=true }) => {
  const toast = useToast();

  const [heatMapData, setHeatMapData] = useState([]);
  const [summaryRingData, setSummaryRingData] = useState();
  // for toggling between outer/inner ring on hover
  const [topLayer, setTopLayer] = useState("outer");

  const getQueryParamsString = () => {
    let year = filters?.year;
    if (!year) {
      // HACK default - which will also get set when filters is initialised
      year = new Date().getFullYear();
    }


    const queryParams = {
      years: [year],
      categories: filters?.categories,
      dataQuality: filters?.dataQuality,
      filterYear: year,
      showSupplierWithoutData: true
    };
    return constructQueryParamsString(queryParams);
  };

  // helper: decide supplier-level completion for a given year
  const completionForYear = (completedCount, progressCount, newCount) => {
    const total = completedCount + progressCount + newCount;
    if (total === 0) return "Not started";
    if (completedCount > 0 && progressCount === 0 && newCount === 0) return "Complete";
    if (progressCount > 0) return "In progress";
    return "Not started";
  };

  // Supplier-level heatmap:
  // - X = supplier-level dataQuality bucket ("Unverified" | "Data Verified" | "Verified")
  // - Y (series id) = completion stage ("Not started" | "Started" | "In progress (data)" | "Complete")
  // - Cell value = number of suppliers in that (completion x dataQuality) bucket
  // - Includes {suppliers:[{supplierId,name}]} for tooltip + precomputed color
  const constructHeatMapData = (suppliers) => {
    const xOrder = ["Verified", "Mixed", "Unverified"];
    const yOrder = ["Not started", "In progress", "Complete"];

    const buckets = {};
    const supplierBuckets = {};

    const dqToX = (dq) => {
      switch (dq) {
        case "VERIFIED":
          return "Verified";
        case "MIXED":
          return "Mixed";
        case "UNVERIFIED":
          return "Unverified";
        default:
          return "Unverified";
      }
    };

    // init all buckets to 0 so cells exist even if empty
    yOrder.forEach((y) => {
      buckets[y] = {};
      supplierBuckets[y] = {};
      xOrder.forEach((x) => {
        buckets[y][x] = 0;
        supplierBuckets[y][x] = [];
      });
    });

    const year = filters?.year || new Date().getFullYear();

    suppliers.forEach(({ yearlyEmissionData = {}, supplierId, supplierName, dataQuality }) => {
      // only use the selected year
      const y = yearlyEmissionData[year];
      if (!y) return; // skip supplier if no data for that year
      const xKey = dqToX(y.dataQuality);

      const completionKeys = {
        "Complete": "completedCount",
        "In progress": "progressCount",
        "Not started": "newCount",
      };

      for (const [completion, countKey] of Object.entries(completionKeys)) {
        const count = y?.[countKey] || 0;
        let completionXKey = xKey;
        if (completion === "In progress" || completion === "Not started") {
          completionXKey = "Unverified";
        }
        if (count > 0) {
          buckets[completion][completionXKey] += count;
          supplierBuckets[completion][completionXKey].push({
            supplierId,
            supplierName,
            count
          });
        }

      }

    });

    // color scale
    const allValues = yOrder.flatMap((y) => xOrder.map((x) => buckets[y][x]));
    const minValue = allValues.length ? Math.min(...allValues) : 0;
    const maxValue = allValues.length ? Math.max(...allValues) : 1;

    // build nivo format (keep stable ordering)
    return yOrder.map((y) => ({
      id: y,
      data: xOrder.map((x) => ({
        x,
        y: buckets[y][x],
        color: calculateLimeGradientColor(buckets[y][x], minValue, maxValue),
        suppliers: supplierBuckets[y][x],
      })),
    }));
  };

  const buildSummaryRings = (data) => {
    const notStartedCount = data.find((d) => d.id === "Not started")?.data.reduce((sum, cell) => sum + cell.y, 0) || 0;
    const inProgressCount = data.find((d) => d.id === "In progress")?.data.reduce((sum, cell) => sum + cell.y, 0) || 0;
    const completeCount = data.find((d) => d.id === "Complete")?.data.reduce((sum, cell) => sum + cell.y, 0) || 0;

    const verified = data.find((d) => d.id === "Complete")?.data.find((cell) => cell.x === "Verified")?.y || 0;
    const total = notStartedCount + inProgressCount + completeCount;

    const outerData = [
      { id: "Not started", value: notStartedCount, color: colors.status.red },
      { id: "In progress", value: inProgressCount, color: colors.status.orange },
      { id: "Finalised", value: completeCount, color: colors.status.green },
    ];

    // inner ring shows verified slice + neutral remainder as track
    const innerData = [
      { id: "Verified", value: verified, color: colors.status.lightGreen },
      { id: "Remainder", value: Math.max(total - verified, 0), color: "lightGray" },
    ];

    // Complete = Finalised
    const finalisedPct = total ? Math.round((completeCount / total) * 100) : 0;
    const verifiedPct = total ? Math.round((verified / total) * 100) : 0;

    return { outerData, innerData, finalisedPct, verifiedPct };
  };

  useEffect(()=> {
    if (heatMapData?.length > 0) {
      setSummaryRingData(buildSummaryRings(heatMapData));
    }
  }, [heatMapData]);

  const { isFetching } = useGetSuppliersYearlyData(getQueryParamsString(), {
    onSuccess: (data) => {
      // exclude data where excludeFromReport is true
      const filteredData = data.filter((item) => !item.excludeFromReport);
      setHeatMapData(constructHeatMapData(filteredData));
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  if (isFetching) {
    return <LocalSpinner />;
  }

  return (
    <Flex h="500px" gap={6}>
      {/* Left: Heatmap (2/3 width) */}
      {showHeatMap && <Box flex="2" h="full">
        <ResponsiveHeatMap
          data={heatMapData}
          margin={{ top: 60, right: 20, bottom: 20, left: 120 }}
          valueFormat=".0f"
          axisTop={{
            tickSize: 0,
            tickPadding: 0,
            legend: "",
            legendPosition: "middle",
            legendOffset: -50,
            truncateTickAt: 0,
            renderTick: (tick) => (
              <g transform={`translate(${tick.x},${tick.y - 32})`}>
                <path d="M-36,-12 L36,-12 Q48,-12 48,0 L48,8 L0,20 L-48,8 L-48,0 Q-48,-12 -36,-12 Z" fill="#051F23" />
                <text y={-2} textAnchor="middle" dominantBaseline="central" style={{ fill: "#FFFFFF", fontSize: 12, fontWeight: 600, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                  {tick.value}
                </text>
              </g>
            ),
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 0,
            tickRotation: 0,
            legend: "",
            legendPosition: "middle",
            legendOffset: -70,
            truncateTickAt: 0,
            renderTick: (tick) => (
              <g transform={`translate(${tick.x - 60},${tick.y})`}>
                <path d="M-43,-12 L43,-12 L55,0 L43,12 L-43,12 Q-55,12 -55,0 Q-55,-12 -43,-12 Z" fill="#85C22A" />
                <text x={-6} textAnchor="middle" dominantBaseline="central" style={{ fill: "#FFFFFF", fontSize: 12, fontWeight: 600, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                  {tick.value}
                </text>
              </g>
            ),
          }}
          axisRight={{ tickSize: 0, tickPadding: 0, tickValues: [] }}
          axisBottom={{ tickSize: 0, tickPadding: 0, tickValues: [] }}
          colors={(d) => d.data?.color?.color ?? "#F7FAFC"}
          labelTextColor={(d) => d.data?.color?.textColor ?? "#E2E8F0"}
          borderWidth={2}
          borderColor="#FFFFFF"
          emptyColor="#F7FAFC"
          tooltip={({ cell }) => <CustomTooltip suppliers={cell?.data?.suppliers} />}
          theme={nivo_theme}
        />
      </Box> }

      {/* Right: Donut summary (1/3 width) */}
      <Box flex="1" position="relative" h="full">
        {/* Inner: VERIFIED ring (under) */}
        <Box position="absolute" inset={0} zIndex={topLayer === "inner" ? 2 : 1}>
          <ResponsivePie
            data={summaryRingData?.innerData}
            colors={{ datum: "data.color" }}
            startAngle={-120}
            endAngle={120}
            innerRadius={0.9}
            padAngle={1}
            cornerRadius={4}
            margin={{ top: 50, right: 40, bottom: 40, left: 40 }}
            enableArcLabels={false}
            enableArcLinkLabels={false}
            activeOuterRadiusOffset={3}
            isInteractive={true}
            layers={["arcs"]}
            onMouseEnter={() => setTopLayer("inner")}
            onMouseLeave={() => setTopLayer("outer")}
            tooltip={({ datum }) => (
              <Box bg="white" borderRadius="md" boxShadow="md" p={2} fontSize="sm" color="gray.800">
                <b>{datum.id}</b>: {datum.value}
              </Box>
            )}
            legends={[]}
          />
        </Box>

        {/* Outer: COMPLETION mix (above) */}
        <Box position="absolute" inset={0} zIndex={topLayer === "outer" ? 2 : 1}>
          <ResponsivePie
            data={summaryRingData?.outerData}
            colors={{ datum: "data.color" }}
            startAngle={-120}
            endAngle={120}
            onMouseEnter={() => setTopLayer("outer")}
            onMouseLeave={() => setTopLayer("inner")}
            innerRadius={0.9}
            padAngle={1}
            cornerRadius={4}
            margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
            enableArcLabels={false}
            enableArcLinkLabels={false}
            activeOuterRadiusOffset={3}
            isInteractive={true}
            layers={["arcs"]}
            tooltip={({ datum }) => (
              <Box bg="white" borderRadius="md" boxShadow="md" p={2} fontSize="sm" color="gray.800">
                <b>{datum.id}</b>: {datum.value}
              </Box>
            )}
            legends={[]}
          />
        </Box>

        {/* Center labels (on top, but ignore pointer events) */}
        <Box
          position="absolute"
          inset={0}
          display="flex"
          flexDir="column"
          alignItems="center"
          justifyContent="center"
          gap="6px"
          zIndex={3}
          pointerEvents="none"
          mt={8}
        >
          <Box fontSize="heading.l" fontWeight="700" color="status.red">
            {summaryRingData?.finalisedPct}%{" "}
            <Box as="span" fontSize="16px" fontWeight="600">
              finalised
            </Box>
          </Box>
          <Box fontSize="heading.l" fontWeight="800" color="teal">
            {summaryRingData?.verifiedPct}%{" "}
            <Box as="span" fontSize="16px" fontWeight="600">
              verified
            </Box>
          </Box>

          {/* Color legends below chart */}
          <Box
            justify="center"
            align="center"
            mt={4}
          >
            {[
              { label: "Not started", color: colors.status.red },
              { label: "In progress", color: colors.status.orange },
              { label: "Finalised", color: colors.status.green },
              { label: "Verified", color: colors.status.lightGreen },
            ].map(({ label, color }) => (
              <Flex key={label} align="center" gap={2}>
                <Box w="14px" h="14px" borderRadius="3px" bg={color} />
                <Box fontSize="sm" color="gray.700">
                  {label}
                </Box>
              </Flex>
            ))}
          </Box>
        </Box>

      </Box>
    </Flex>
  );
};

const CustomTooltip = ({ suppliers }) => (
  <Flex p={2} gap={0} direction="column" borderRadius="md" bg="pureWhite" border="1px solid" borderColor="lime">
    {suppliers?.map((s) => (
      <Text m={0} key={s?.supplierId} p={1} color="darkTeal" fontSize="sm">
        {s?.supplierName} ({s?.count})
      </Text>
    ))}
  </Flex>
);

export default SupplierListVisual;
